import { requestJson } from "./apiClient";
import { NEWS_CONFIG } from "../config/newsConfig";

const normalizeAdDecision = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const campaignId = Number(value.campaign_id);
  const creativeId = Number(value.creative_id);
  const creativeUrl = String(value.creative_url || "").trim();
  const targetUrl = String(value.target_url || "").trim();

  if (
    !Number.isFinite(campaignId) ||
    campaignId <= 0 ||
    !Number.isFinite(creativeId) ||
    creativeId <= 0 ||
    !creativeUrl ||
    !targetUrl
  ) {
    return null;
  }

  return {
    campaignId,
    creativeId,
    brandName: String(value.brand_name || "").trim(),
    creativeUrl,
    targetUrl,
    altText: String(value.alt_text || "").trim(),
  };
};

const normalizeHouseAd = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const targetUrl = String(value.target_url || "").trim();
  const title = String(value.title || "").trim();
  const subtitle = String(value.subtitle || "").trim();
  const ctaLabel = String(value.cta_label || "").trim();

  let targetIsSafe = false;

  try {
    const parsed = new URL(targetUrl);
    targetIsSafe =
      (parsed.protocol === "http:" || parsed.protocol === "https:") &&
      !parsed.username &&
      !parsed.password;
  } catch (_error) {
    targetIsSafe = false;
  }

  const enabled = Boolean(value.enabled) && targetIsSafe && Boolean(title) && Boolean(ctaLabel);

  return {
    enabled,
    firstEnabled: enabled && Boolean(value.first_enabled),
    regularEnabled: enabled && Boolean(value.regular_enabled),
    title,
    subtitle,
    ctaLabel,
    targetUrl: targetIsSafe ? targetUrl : "",
    version: String(value.version || "").trim(),
  };
};

const normalizeAdPlan = (response) => {
  const data = response?.data;

  if (!response?.success || !data || typeof data !== "object") {
    throw new Error("Invalid advertising plan response");
  }

  // The backend owns which placement types are active on each surface.
  // Missing flags default to true for backward compatibility with API builds
  // that predate the explicit Home/Focus surface contract.
  const firstEnabled = data?.placement?.first_enabled !== false;
  const regularEnabled = data?.placement?.regular_enabled !== false;
  const regularEvery = Number(data?.placement?.regular_every);
  const cursor = Number(data.cursor);
  const nextCursor = Number(data.next_cursor);
  const regular =
    regularEnabled && Array.isArray(data.regular)
      ? data.regular.map(normalizeAdDecision)
      : [];

  return {
    enabled: Boolean(data.enabled),
    sessionId: String(data.session_id || "").trim(),
    version: String(data.version || "").trim(),
    expiresAt: String(data.expires_at || "").trim(),
    ttlSeconds: Math.max(0, Number(data.ttl_seconds) || 0),
    regularEvery:
      regularEnabled &&
      Number.isFinite(regularEvery) &&
      regularEvery > 0
        ? Math.floor(regularEvery)
        : 0,
    cursor:
      Number.isFinite(cursor) && cursor >= 0
        ? Math.floor(cursor)
        : 0,
    nextCursor:
      Number.isFinite(nextCursor) && nextCursor >= 0
        ? Math.floor(nextCursor)
        : regular.length,
    first: firstEnabled ? normalizeAdDecision(data.first) : null,
    regular,
    houseAd: normalizeHouseAd(data.house_ad),
  };
};

export const fetchAdPlan = async ({
  surface,
  device,
  sessionId,
  batch,
  cursor = 0,
  signal,
}) => {
  const params = new URLSearchParams();

  params.set("type", "ads_plan");
  params.set("surface", surface);
  params.set("device", device);
  params.set("session_id", sessionId);
  params.set("batch", String(batch));
  params.set("cursor", String(cursor));

  const response = await requestJson({
    url: `${NEWS_CONFIG.apiUrl}?${params.toString()}`,
    operation: "ads-plan",
    signal,
    timeoutMs: 5000,
    defaultMessage: "دریافت تبلیغات با مشکل مواجه شد",
  });

  return normalizeAdPlan(response);
};

export const recordAdBannerEvent = async ({
  campaignId = 0,
  creativeId = 0,
  event,
  surface,
  device,
  placement,
  bannerType = "commercial",
}) => {
  const normalizedType = String(bannerType || "commercial").trim().toLowerCase();
  const normalizedEvent = String(event || "").trim().toLowerCase();
  const normalizedSurface = String(surface || "").trim().toLowerCase();
  const normalizedDevice = String(device || "").trim().toLowerCase();
  const normalizedPlacement = String(placement || "").trim().toLowerCase();
  const normalizedCampaignId = Number(campaignId);
  const normalizedCreativeId = Number(creativeId);

  if (
    !["shown", "clicked", "failed"].includes(normalizedEvent) ||
    !["home", "focus"].includes(normalizedSurface) ||
    !["mobile", "desktop"].includes(normalizedDevice) ||
    !["first", "regular"].includes(normalizedPlacement) ||
    !["commercial", "house"].includes(normalizedType)
  ) {
    return;
  }

  if (normalizedType === "commercial") {
    if (
      !Number.isSafeInteger(normalizedCampaignId) ||
      normalizedCampaignId < 1 ||
      !Number.isSafeInteger(normalizedCreativeId) ||
      normalizedCreativeId < 1
    ) {
      return;
    }
  } else if (normalizedCampaignId !== 0 || normalizedCreativeId !== 0) {
    return;
  }

  try {
    await requestJson({
      url: `${NEWS_CONFIG.apiUrl}?type=ads_banner_event`,
      operation: "ads-banner-event",
      timeoutMs: 3000,
      defaultMessage: "ثبت رویداد بنر با مشکل مواجه شد",
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        campaign_id: normalizedType === "house" ? 0 : normalizedCampaignId,
        creative_id: normalizedType === "house" ? 0 : normalizedCreativeId,
        banner_type: normalizedType,
        event: normalizedEvent,
        surface: normalizedSurface,
        device: normalizedDevice,
        placement: normalizedPlacement,
      }),
    });
  } catch (_error) {
    // Banner analytics is best-effort and must never block or alter ad UI.
  }
};
